import React, { useState } from 'react'
import { CalendarPlus, ChevronRight, Lightbulb } from 'lucide-react'
import { toast } from '@/components/ui/sonner'
import {
  useGetIdeasQuery,
  useUpdatePlannedPostMutation,
  type PlannedPost,
} from '@/redux/collaborationsApi'
import { fieldCls, fmt, localToday } from './postHelpers'

/**
 * Ideas with no post date yet. Pick a day and the idea becomes a planned post, with its shoot
 * and edit days backed in.
 */
export const IdeaBank: React.FC<{
  onOpen: (p: PlannedPost) => void
  onChanged?: () => void
}> = ({ onOpen, onChanged }) => {
  const { data } = useGetIdeasQuery()
  const [update] = useUpdatePlannedPostMutation()
  const [picking, setPicking] = useState<string | null>(null)
  const [day, setDay] = useState('')
  const ideas = data?.ideas ?? []
  if (!ideas.length) return null

  const schedule = async (p: PlannedPost) => {
    if (!day) return
    try {
      const saved = await update({ id: p.id, plannedOn: day }).unwrap()
      toast('Scheduled', {
        description: `Shoot ${saved.shootOn ? fmt(saved.shootOn, { weekday: 'short', month: 'short', day: 'numeric' }) : ''}, edit ${saved.editOn ? fmt(saved.editOn, { weekday: 'short' }) : ''}, post ${fmt(day, { weekday: 'short', month: 'short', day: 'numeric' })}.`,
      })
      setPicking(null)
      setDay('')
      onChanged?.()
    } catch (err: any) {
      toast.error('Couldn’t schedule it', { description: err?.data?.error ?? err?.message })
    }
  }

  return (
    <section className="space-y-2">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
        <Lightbulb className="h-3.5 w-3.5" /> Idea bank · {ideas.length}
      </p>
      <ul className="space-y-2">
        {ideas.map((p) => (
          <li key={p.id} className="rounded-2xl border border-border bg-card p-3 shadow-sm">
            <button
              type="button"
              onClick={() => onOpen(p)}
              className="flex w-full items-start gap-2 text-left"
            >
              <span className="min-w-0 flex-1">
                {p.theme?.label ? (
                  <span className="block text-xs font-semibold text-[#3F7A0A]">
                    {p.theme.label}
                  </span>
                ) : null}
                <span className="block font-semibold text-foreground">
                  {p.idea ?? p.hook ?? 'Idea'}
                </span>
                {p.hook && p.idea ? (
                  <span className="block text-sm italic text-muted-foreground">“{p.hook}”</span>
                ) : null}
              </span>
              <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
            {picking === p.id ? (
              <div className="mt-2 flex gap-2">
                <input
                  type="date"
                  min={localToday()}
                  value={day}
                  onChange={(e) => setDay(e.target.value)}
                  aria-label="Post day"
                  className={fieldCls}
                />
                <button
                  type="button"
                  disabled={!day}
                  onClick={() => schedule(p)}
                  className="h-11 shrink-0 rounded-xl bg-[#C0FD72] px-4 text-sm font-semibold text-[#0F172A] disabled:opacity-50"
                >
                  Schedule
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setPicking(p.id)
                  setDay('')
                }}
                className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#3F7A0A]"
              >
                <CalendarPlus className="h-4 w-4" /> Pick a post day
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

export default IdeaBank
